// Used by the route search page to tell <LeadBot /> what the user is going through.
// SearchForm stays in charge of its data; the bot only reads these signals.
import { useEffect } from 'react';
import { setLeadSignals, setLeadBotSuppressed, resetPageSignals } from './leadBotStore';

const DEEP_SCROLL_SCREENS = 2.5;   // scrolled ~2.5 screens of results...
const DEEP_SCROLL_MIN_MS = 30000;  // ...and spent 30s+ without clicking Book
const AWAY_MS = 20000;             // came back after 20s+ in another tab (booking tab)

export const useLeadBotSignals = ({
    search,
    searchKey,
    routeKey,
    resultsLoading,
    resultsCount,
    noRoutes,
    allWaitlisted,
    tatkalWindow,
    suppress = {},
}) => {
    // Page lifecycle
    useEffect(() => {
        setLeadSignals({ page: 'search' });
        return () => resetPageSignals();
    }, []);

    // Current search (keyed so we only publish when it actually changes)
    useEffect(() => {
        setLeadSignals({ search, searchKey });
    }, [searchKey]); // eslint-disable-line react-hooks/exhaustive-deps

    // Date flips count per route, not per date
    useEffect(() => {
        setLeadSignals({ dateFlips: 0 });
    }, [routeKey]);

    useEffect(() => {
        setLeadSignals({ resultsLoading, resultsCount, noRoutes, allWaitlisted, tatkalWindow });
    }, [resultsLoading, resultsCount, noRoutes, allWaitlisted, tatkalWindow]);

    const suppressKey = JSON.stringify(suppress);
    useEffect(() => {
        setLeadBotSuppressed(suppress);
    }, [suppressKey]); // eslint-disable-line react-hooks/exhaustive-deps

    // Behaviour signals, reset for every new search
    useEffect(() => {
        setLeadSignals({ deepScrollNoBook: false, returnedAfterAway: false });
        const start = Date.now();
        let maxY = 0;
        let bookClicked = false;
        let hiddenAt = null;

        const onScroll = () => {
            maxY = Math.max(maxY, window.scrollY);
            if (!bookClicked
                && maxY > window.innerHeight * DEEP_SCROLL_SCREENS
                && Date.now() - start > DEEP_SCROLL_MIN_MS) {
                setLeadSignals({ deepScrollNoBook: true });
            }
        };

        // Any Book / Book now button inside the results counts as a booking attempt
        const onClick = (e) => {
            const el = e.target && e.target.closest ? e.target.closest('button, a') : null;
            if (el && /^\s*book/i.test(el.textContent || '')) {
                bookClicked = true;
                setLeadSignals({ deepScrollNoBook: false });
            }
        };

        const onVisibility = () => {
            if (document.hidden) {
                hiddenAt = Date.now();
            } else if (hiddenAt && Date.now() - hiddenAt > AWAY_MS) {
                setLeadSignals({ returnedAfterAway: true });
                hiddenAt = null;
            }
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        document.addEventListener('click', onClick, true);
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            window.removeEventListener('scroll', onScroll);
            document.removeEventListener('click', onClick, true);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [searchKey]);
};

export default useLeadBotSignals;
